<template>
  <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6" data-testid="overview-kpi-strip">
    <div
      class="overview-kpi-tile flex flex-col gap-2 min-h-[7.5rem] relative"
      data-testid="overview-kpi-ppc"
    >
      <div class="flex items-start justify-between gap-2">
        <span class="overview-kpi-tile__label">
          {{ $t('UserProjects.overview.kpi.ppc.label') }}
        </span>
        <span
          v-if="showPpcAmpel"
          class="overview-kpi-tile__ampel"
          :class="ppcAmpelClass"
          aria-hidden="true"
        />
      </div>

      <template v-if="ppcLoading">
        <span class="text-sm text-[var(--color-disabled)]">{{ $t('UserProjects.overview.loading') }}</span>
      </template>

      <template v-else-if="ppcError">
        <span class="text-sm text-[var(--color-error-text)]">{{ ppcError }}</span>
      </template>

      <template v-else-if="ppcInsufficientSample">
        <span class="overview-kpi-tile__subtext text-[var(--color-disabled)] py-2">
          {{ $t('UserProjects.overview.kpi.ppc.insufficientSample') }}
        </span>
      </template>

      <template v-else-if="ppcEmpty">
        <span class="text-3xl font-semibold text-[var(--color-disabled)]">—</span>
        <span class="text-sm text-[var(--color-disabled)]">
          {{ $t('UserProjects.overview.kpi.ppc.empty') }}
        </span>
      </template>

      <template v-else>
        <span class="overview-kpi-tile__value">{{ ppcPercent }}%</span>
        <span class="overview-kpi-tile__subtext" :class="trendClass">
          {{ trendLabel }}
        </span>
        <span class="text-xs text-[var(--color-disabled)] overview-kpi-tile__subtext">
          {{ $t('UserProjects.overview.kpi.ppc.sample', { n: commitmentCount }) }}
        </span>
      </template>
    </div>

    <div
      class="overview-kpi-tile flex flex-col gap-2 min-h-[7.5rem] relative"
      data-testid="overview-kpi-open-commitments"
    >
      <div class="flex items-start justify-between gap-2">
        <span class="overview-kpi-tile__label">
          {{ $t('UserProjects.overview.kpi.openCommitments.label') }}
        </span>
        <span
          class="overview-kpi-tile__ampel"
          :class="openCommitmentsAmpel"
          aria-hidden="true"
        />
      </div>
      <span class="overview-kpi-tile__value">{{ projectOpenCount }}</span>
      <span class="overview-kpi-tile__subtext text-[var(--color-error-text)]">
        {{ $t('UserProjects.overview.kpi.openCommitments.overdue', { count: projectOverdueCount }) }}
      </span>
    </div>

    <div
      class="overview-kpi-tile flex flex-col gap-2 min-h-[7.5rem] relative"
      data-testid="overview-kpi-milestones"
    >
      <div class="flex items-start justify-between gap-2">
        <span class="overview-kpi-tile__label">
          {{ $t('UserProjects.overview.kpi.milestones.label') }}
        </span>
        <span
          class="overview-kpi-tile__ampel"
          :class="milestonesAmpel"
          aria-hidden="true"
        />
      </div>
      <span class="overview-kpi-tile__value">
        {{ completedMilestonesCount }} / {{ totalMilestonesCount }}
      </span>
      <span class="overview-kpi-tile__subtext text-[var(--color-disabled)]">
        {{ nextMilestoneSubtitle }}
      </span>
    </div>

    <OverviewConstraintsDiffTile />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import OverviewConstraintsDiffTile from './OverviewConstraintsDiffTile.vue';
import {
  formatPpcTrend,
  hasPpcSample,
  hasPpcValue,
  milestonesAmpelClass,
  openCommitmentsAmpelClass,
  ppcAmpelBgClass,
  ppcSeverityClass,
} from '@/utils/ppcDisplay';

export default {
  name: 'OverviewKpiStrip',
  components: {
    OverviewConstraintsDiffTile,
  },
  computed: {
    ...mapGetters('pmCommitment', {
      overview: 'overview',
    }),
    ...mapGetters('pmPpc', {
      ppcLoading: 'loading',
      ppcError: 'error',
      currentWeek: 'currentWeek',
      ppcPercent: 'ppcPercent',
      commitmentCount: 'commitmentCount',
      trendDelta: 'trendDelta',
      trendDirection: 'trendDirection',
      severity: 'severity',
      ppcEmpty: 'isEmpty',
    }),
    projectOpenCount() {
      return this.overview?.totals?.projectOpenCount ?? 0;
    },
    projectOverdueCount() {
      return this.overview?.totals?.projectOverdueCount ?? 0;
    },
    completedMilestonesCount() {
      return this.overview?.totals?.completedMilestonesCount ?? 0;
    },
    totalMilestonesCount() {
      return this.overview?.totals?.totalMilestonesCount ?? 0;
    },
    overdueMilestonesCount() {
      return this.overview?.totals?.overdueMilestonesCount ?? 0;
    },
    ppcInsufficientSample() {
      return hasPpcValue(this.currentWeek) && !hasPpcSample(this.currentWeek);
    },
    showPpcAmpel() {
      return hasPpcSample(this.currentWeek) && !this.ppcEmpty && !this.ppcLoading;
    },
    ppcAmpelClass() {
      return ppcAmpelBgClass(this.severity);
    },
    openCommitmentsAmpel() {
      return openCommitmentsAmpelClass(this.projectOverdueCount, this.projectOpenCount);
    },
    milestonesAmpel() {
      return milestonesAmpelClass(
        this.overdueMilestonesCount,
        this.totalMilestonesCount,
        this.completedMilestonesCount,
      );
    },
    nextMilestoneSubtitle() {
      const next = this.overview?.nextMilestone;
      if (!next) {
        return this.$t('UserProjects.overview.kpi.milestones.none');
      }
      return this.$t('UserProjects.overview.kpi.milestones.next', {
        title: next.title,
        date: this.formatDate(next.dueDate),
      });
    },
    trendClass() {
      return ppcSeverityClass(this.severity);
    },
    trendLabel() {
      if (!hasPpcValue(this.currentWeek)) {
        return '';
      }
      const trend = formatPpcTrend(this.trendDelta, this.trendDirection);
      if (!trend) {
        return this.$t('UserProjects.overview.kpi.ppc.noTrend');
      }
      return this.$t('UserProjects.overview.kpi.ppc.trendVsWeek', { trend });
    },
  },
  methods: {
    formatDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleDateString();
    },
  },
};
</script>

<style scoped>
.overview-kpi-tile {
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 14px;
  padding: 1.25rem;
  min-width: 0;
}

.overview-kpi-tile__label {
  font-size: 13px;
  color: var(--color-disabled);
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.35;
  min-width: 0;
  flex: 1;
}

.overview-kpi-tile__value {
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
  color: var(--color-dark);
}

.overview-kpi-tile__subtext {
  font-size: 13px;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.35;
}

.overview-kpi-tile__ampel {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  flex-shrink: 0;
  margin-top: 2px;
}
</style>
